// ─────────────────────────────────────────────────────────────────────────────
// cadence_engine.ts — Short/long tap-rhythm matcher
//
// Conceptual model
// ────────────────
// The engine maintains a single growing buffer of taps + a derived letter
// string. Each new gap between consecutive taps is classified by ONE
// absolute threshold:
//
//   gap < SL_THRESHOLD_MS  → S (short pulse)
//   gap ≥ SL_THRESHOLD_MS  → L (long pulse)
//
// Patterns are exact letter strings ('S', 'SL', 'LS', …) — tap count
// is sequence length + 1. When the accumulated letters match a
// pattern, `onResolved` fires. The engine does NOT reset on resolve,
// so a longer pattern can fire on top of a previously-fired shorter
// one (e.g. 'S' resolves the cast, then a third tap with a long gap
// resolves 'SL' for the stylus menu — same chain). Multiple patterns
// can share an id ('stylus-summon' is registered as both 'SL' and
// 'LS') so the same invocation can have several rhythmic spellings.
//
// State only resets on:
//   - timeout (no follow-up tap within the per-state window)
//   - spatial drift (a tap landed too far from the chain anchor)
//   - explicit `reset()` (page switch, etc.)
//
// Pen-down freeze
// ───────────────
// While the user is mid-stroke (pen down), the chain timer is paused
// via `pause()`. This prevents `onSingleTap` / `onChainEnd` from firing
// in the middle of a long pen stroke, which would race with the spell
// engine and produce confusing side effects. `resume()` re-arms the
// timer with a full window once the pen lifts (so the user gets a
// fresh patience window from pen-up, not from the original tap).
// `feedTap` implicitly unpauses since a new tap re-anchors the chain.
//
// Callbacks
// ─────────
//   onTapAccepted — every accepted tap. Reports the pulse that *led*
//                   to this tap (null on the first tap). Used by the
//                   canvas to colour a per-pulse ring + audio note.
//   onResolved    — the accumulated letters matched a pattern. Used
//                   to spawn the invocation (cast, stylus menu, …).
//   onSingleTap   — a lone tap with no follow-up before the short
//                   window expired. Used as a fallback action.
//   onChainEnd    — a multi-tap chain timed out without further taps.
//                   Used to dismiss any cast that the chain put on.
// ─────────────────────────────────────────────────────────────────────────────

// ── Pattern definition ───────────────────────────────────────────────────────

/** A cadence invocation. The `sequence` is a string of S/L letters
 *  describing the inter-tap gaps; tap count = sequence.length + 1. */
export interface CadencePattern {
  id: string;
  /** S = short gap, L = long gap. Empty string is not used by the
   *  matcher — single-tap fallbacks come through onSingleTap. */
  sequence: string;
  spreadPx: number;
  priority: number;
}

/** S = short pulse, L = long pulse. Null on the very first tap of a
 *  sequence (no preceding gap). */
export type PulseType = 'S' | 'L' | null;

// ── Tunables ────────────────────────────────────────────────────────────────

/** Single absolute threshold separating short from long. No dead zone.
 *  Exported because the same span is also the natural tap-vs-writing
 *  cooldown — if the user has been idle longer than one short-pulse
 *  worth, the next short touch is a tap candidate again. Keeping the
 *  two concepts on a single constant prevents drift. */
export const SL_THRESHOLD_MS = 300;

/** A gap longer than this resets the sequence — the user has clearly
 *  moved on from the previous chain. Also the wait window after any
 *  tap past the first; once it expires the chain ends.
 *
 *  Kept tight (750 ms) so an unconscious pause between tap attempts
 *  doesn't accidentally extend the previous chain into a new pattern.
 *  L gaps thus live in [SL_THRESHOLD_MS, LONG_MAX_MS] = [300, 750] ms. */
const LONG_MAX_MS = 750;

/** After the first tap, the engine waits for a follow-up before
 *  declaring a single-tap. Two windows depending on registered
 *  patterns:
 *
 *    SINGLE_TAP_S_FIRST_MS — short. Used when every registered
 *      pattern starts with an S gap; an L follow-up couldn't begin
 *      any pattern, so we settle to single-tap as soon as the
 *      short-gap window closes. Snappier dismiss/caret behaviour.
 *
 *    SINGLE_TAP_L_FIRST_MS — long. Used when at least one pattern
 *      starts with L (e.g. 'LS' for stylus menu). The first gap can
 *      legitimately be long, so we have to wait the full continuation
 *      window before deciding the chain was a lone tap.
 *
 *  The choice is computed per-tap from the registered pattern set. */
const SINGLE_TAP_S_FIRST_MS = SL_THRESHOLD_MS;
const SINGLE_TAP_L_FIRST_MS = LONG_MAX_MS;

/** After tap 2+, wait the full LONG_MAX_MS before giving up — chains
 *  mid-flight can have any inter-tap gap up to that cap. */
const CONTINUATION_TIMEOUT_MS = LONG_MAX_MS;

/** Spatial spread cap (px) when no per-pattern spread overrides. */
const DEFAULT_SPREAD_PX = 80;

// ── Internal types ──────────────────────────────────────────────────────────

interface TapEntry {
  x: number;
  y: number;
  t: number;
}

/** Public alias for callers that consume the resolved-pattern taps array
 *  (e.g. `seam_engine` reading rhythm phase + weight from the cadence).
 *  Same shape as `TapEntry`. */
export type TapRecord = TapEntry;

// ── Engine ──────────────────────────────────────────────────────────────────

export class CadenceEngine {
  private _patterns: CadencePattern[] = [];
  private _seq: TapEntry[] = [];
  private _letters = '';
  private _timer = 0;
  private _paused = false;

  // ── Callbacks ──────────────────────────────────────────────────────────
  onResolved: ((patternId: string, taps: TapEntry[]) => void) | null = null;
  onSingleTap: ((x: number, y: number) => void) | null = null;
  onChainEnd: (() => void) | null = null;
  /** Fired for every accepted tap. `pulse` is the gap that led to this
   *  tap ('S' or 'L'), or null on the first tap of a sequence. */
  onTapAccepted: ((tapIndex: number, patternId: string | null, pulse: PulseType, x: number, y: number) => void) | null = null;

  register(pattern: CadencePattern): void {
    this._patterns.push(pattern);
    this._patterns.sort((a, b) => b.priority - a.priority);
  }

  /** Feed a confirmed tap (called at pointer-up when the touch passed
   *  isTap()). Resolves synchronously on the resolving tap when the
   *  accumulated letters match a pattern. */
  feedTap(x: number, y: number, t: number): void {
    clearTimeout(this._timer);
    this._paused = false;

    let pulse: PulseType = null;

    if (this._seq.length > 0) {
      // Spatial drift kills the chain.
      const anchor = this._seq[0];
      const dist = Math.hypot(x - anchor.x, y - anchor.y);
      if (dist > this._patternSpread()) {
        this._reset();
      }
    }

    if (this._seq.length > 0) {
      const gap = t - this._seq[this._seq.length - 1].t;
      if (gap > LONG_MAX_MS) {
        // Stale — abandon the previous chain; this tap restarts.
        this._reset();
      } else {
        pulse = gap < SL_THRESHOLD_MS ? 'S' : 'L';
        this._letters += pulse;
      }
    }

    this._seq.push({ x, y, t });
    const tapIndex = this._seq.length - 1;

    const matched = this._matchSequence(this._letters);
    if (matched) {
      this.onTapAccepted?.(tapIndex, matched.id, pulse, x, y);
      this._fireResolved(matched);
    } else {
      this.onTapAccepted?.(tapIndex, null, pulse, x, y);
    }

    this._armTimer();
  }

  /** Pause the chain timer (e.g. on pen-down for a non-tap stroke).
   *  No-op if no chain is active. Idempotent. */
  pause(): void {
    if (this._paused) return;
    this._paused = true;
    clearTimeout(this._timer);
  }

  /** Resume the chain timer with a fresh window from now (so the user
   *  gets a full tap-follow-up window after lifting the pen). No-op
   *  if not paused or no chain is active. */
  resume(): void {
    if (!this._paused) return;
    this._paused = false;
    if (this._seq.length === 0) return;
    this._armTimer();
  }

  /** Reset state (e.g. on view switch). */
  reset(): void {
    clearTimeout(this._timer);
    this._paused = false;
    this._reset();
  }

  /** Mid-sequence indicator (for callers that want to suppress drawing
   *  affordances while a tap chain is being recognised). */
  get active(): boolean {
    return this._seq.length > 0;
  }

  /** Live letter sequence — for HUD overlays that show the user what
   *  the engine has classified so far. Empty when no chain is active. */
  get letters(): string {
    return this._letters;
  }

  /** Position of the most recent tap, for HUD anchor. Null when no
   *  chain is active. */
  get lastTap(): { x: number; y: number } | null {
    if (this._seq.length === 0) return null;
    const t = this._seq[this._seq.length - 1];
    return { x: t.x, y: t.y };
  }

  /** Look ahead: if a tap landed at (x, y, t) right now, would it
   *  resolve a pattern? Used by the canvas to fire invocation feedback
   *  at pen-DOWN — much snappier than waiting for the pen-up feedTap.
   *  Returns the matched pattern id, or null if no resolve is imminent.
   *
   *  This does NOT mutate any state; the actual feedTap at pen-up is
   *  still authoritative. If the touch turns out to be a stroke
   *  instead of a tap, the early audio is a small false positive. */
  peekResolve(x: number, y: number, t: number): string | null {
    if (this._seq.length === 0) return null;
    // Spatial drift would reset the chain.
    const anchor = this._seq[0];
    if (Math.hypot(x - anchor.x, y - anchor.y) > this._patternSpread()) return null;
    // Stale gap would reset the chain.
    const gap = t - this._seq[this._seq.length - 1].t;
    if (gap > LONG_MAX_MS) return null;
    const pulse: PulseType = gap < SL_THRESHOLD_MS ? 'S' : 'L';
    const predicted = this._letters + pulse;
    // Spread check against the existing seq is a sufficient
    // approximation — adding (x, y) can only make the spread larger,
    // and we already verified (x, y) sits inside the spread cap from
    // the anchor, so the new tap can't blow past the cap on its own.
    for (const p of this._patterns) {
      if (p.sequence === predicted) return p.id;
    }
    return null;
  }

  // ── Internals ──────────────────────────────────────────────────────────

  private _reset(): void {
    this._seq = [];
    this._letters = '';
  }

  private _armTimer(): void {
    clearTimeout(this._timer);
    if (this._seq.length === 0) return;
    let ms: number;
    if (this._seq.length === 1) {
      // First-tap window depends on whether any pattern can start
      // with an L gap. If yes, we must wait the full continuation
      // window so a long first gap has a chance to land.
      ms = this._hasLFirstPattern()
        ? SINGLE_TAP_L_FIRST_MS
        : SINGLE_TAP_S_FIRST_MS;
    } else {
      ms = CONTINUATION_TIMEOUT_MS;
    }
    this._timer = window.setTimeout(() => this._fireTimeout(), ms);
  }

  private _hasLFirstPattern(): boolean {
    for (const p of this._patterns) {
      if (p.sequence.length > 0 && p.sequence[0] === 'L') return true;
    }
    return false;
  }

  private _patternSpread(): number {
    let max = DEFAULT_SPREAD_PX;
    for (const p of this._patterns) {
      if (p.spreadPx > max) max = p.spreadPx;
    }
    return max;
  }

  private _matchSequence(letters: string): CadencePattern | null {
    let best: CadencePattern | null = null;
    for (const p of this._patterns) {
      if (p.sequence !== letters) continue;
      if (!this._withinSpread(p.spreadPx)) continue;
      if (!best || p.priority > best.priority) best = p;
    }
    return best;
  }

  private _withinSpread(maxPx: number): boolean {
    if (this._seq.length < 2) return true;
    const a = this._seq[0];
    for (let i = 1; i < this._seq.length; i++) {
      if (Math.hypot(this._seq[i].x - a.x, this._seq[i].y - a.y) > maxPx) return false;
    }
    return true;
  }

  private _fireResolved(pattern: CadencePattern): void {
    const taps = this._seq.slice();
    this.onResolved?.(pattern.id, taps);
  }

  /** Timer fired — chain is over. Solo first-tap fires onSingleTap;
   *  multi-tap chain fires onChainEnd. Either way the buffer is reset. */
  private _fireTimeout(): void {
    clearTimeout(this._timer);
    if (this._paused) return;
    if (this._seq.length === 0) return;

    if (this._seq.length === 1) {
      const tap = this._seq[0];
      this._reset();
      this.onSingleTap?.(tap.x, tap.y);
      return;
    }

    this._reset();
    this.onChainEnd?.();
  }
}
